// A store's sections in walking order. Drag by the handle, or use the up/down buttons
// (keyboard and screen readers); every move is announced.
import { useRef, useState } from 'react';
import { ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import { moveInList, type Category } from '@milk/shared';

export function AisleOrder({ categories, onChange }: { categories: Category[]; onChange(ids: string[]): void }) {
  const [drag, setDrag] = useState<{ from: number; over: number } | null>(null);
  const [said, setSaid] = useState('');
  const listRef = useRef<HTMLOListElement>(null);
  const ids = categories.map((c) => c.id);

  const move = (i: number, delta: number) => {
    const next = moveInList(ids, i, delta);
    if (next === ids) return;
    onChange(next);
    setSaid(`${categories[i].name} moved to position ${i + delta + 1} of ${ids.length}`);
    // keep focus on the same button of the moved row
    const dir = delta < 0 ? 'up' : 'down';
    requestAnimationFrame(() => {
      const b = listRef.current?.querySelector<HTMLButtonElement>(`li:nth-child(${i + delta + 1}) button[data-dir="${dir}"]`);
      if (b && !b.disabled) b.focus(); else listRef.current?.querySelector<HTMLButtonElement>(`li:nth-child(${i + delta + 1}) button:not(:disabled)`)?.focus();
    });
  };

  const indexAt = (y: number) => {
    const rows = [...(listRef.current?.children ?? [])] as HTMLElement[];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i].getBoundingClientRect();
      if (y < r.top + r.height / 2) return i;
    }
    return rows.length - 1;
  };

  return (
    <>
      <ol className="aisle" ref={listRef} aria-label="Sections in walking order">
        {categories.map((c, i) => (
          <li key={c.id} className={`${drag?.from === i ? 'dragging' : ''}${drag && drag.over === i && drag.from !== i ? ' over' : ''}`}>
            <span
              className="grip"
              aria-hidden
              onPointerDown={(e) => { (e.target as HTMLElement).setPointerCapture(e.pointerId); setDrag({ from: i, over: i }); }}
              onPointerMove={(e) => { if (drag) setDrag({ ...drag, over: indexAt(e.clientY) }); }}
              onPointerUp={() => { if (drag && drag.over !== drag.from) move(drag.from, drag.over - drag.from); setDrag(null); }}
              onPointerCancel={() => setDrag(null)}
            >
              <GripVertical size={18} />
            </span>
            <span className="label"><span aria-hidden>{c.emoji}</span> {c.name}</span>
            <button type="button" className="icon-btn" data-dir="up" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Move ${c.name} up`}><ChevronUp size={20} aria-hidden /></button>
            <button type="button" className="icon-btn" data-dir="down" disabled={i === categories.length - 1} onClick={() => move(i, 1)} aria-label={`Move ${c.name} down`}><ChevronDown size={20} aria-hidden /></button>
          </li>
        ))}
      </ol>
      <p className="sr-only" role="status" aria-live="polite">{said}</p>
    </>
  );
}
